import { creatorbase, Currency } from '@round/api';
import { CSSProperties, useMemo } from 'react';
import { Column } from 'react-table';
import isFinite from 'lodash/isFinite';
import ClipboardTable from 'utility/copyToClipboard/ClipboardTable/ClipboardTable';
import { postStatusLabels } from '../../../Posts/helpers';
import { asMoney, formatDateShort, numberWithCommas } from 'helpers';
import { toDecimalPoint } from '@round/utils';

export type Row = {
    status: creatorbase.PostStatus;
    cost: number | undefined | null;
    views: number | undefined | null;
    likes: number | undefined | null;
    comments: number | undefined | null;
    channel: string | undefined | null;
    link: string | undefined | null;
    engagementRate: number | undefined | null;
    uploadDate: string | undefined | null;
};

export type Totals = Pick<Row, 'views' | 'likes' | 'comments' | 'engagementRate' | 'cost'>;

export type Props = {
    items: Row[];
    totals?: Totals;
    artistName: string | undefined;
    projectName: string | undefined;
    campaignName: string | undefined;
    currency: Currency | undefined;
    includeCost?: boolean;
};

const tfootStyles: CSSProperties = {
    backgroundColor: '#eeeeee',
};

const tdStyles: CSSProperties = {
    border: '1px solid black',
};

const totalsCellStyles: CSSProperties = {
    fontWeight: 'bold',
    padding: '5px',
};

const YoutubeReportClipboardTable = ({
    items,
    artistName,
    projectName,
    campaignName,
    currency,
    totals,
    includeCost = true,
}: Props) => {
    const columns = useMemo<Column<Row>[]>(
        () => [
            {
                Header: () => <div style={{ textAlign: 'left' }}>{campaignName}</div>,
                accessor: 'channel',
                Cell: ({ value }) => <>{value ?? '-'}</>,
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ value }) => <>{postStatusLabels[value]}</>,
            },
            {
                Header: 'Uploaded',
                accessor: 'uploadDate',
                Cell: ({ value }) => <>{typeof value === 'string' ? formatDateShort(value) : '-'}</>,
            },
            {
                Header: 'Cost',
                accessor: 'cost',
                Cell: ({ value }) => (
                    <ClipboardTable.Stats>
                        {typeof value === 'number' ? asMoney(value, currency) : '-'}
                    </ClipboardTable.Stats>
                ),
            },
            {
                Header: 'Views',
                accessor: 'views',
                Cell: ({ value }) => (
                    <ClipboardTable.Stats>
                        {typeof value === 'number' ? numberWithCommas(value) : '-'}
                    </ClipboardTable.Stats>
                ),
            },
            {
                Header: 'Likes',
                accessor: 'likes',
                Cell: ({ value }) => (
                    <ClipboardTable.Stats>
                        {typeof value === 'number' ? numberWithCommas(value) : '-'}
                    </ClipboardTable.Stats>
                ),
            },
            {
                Header: 'Comments',
                accessor: 'comments',
                Cell: ({ value }) => (
                    <ClipboardTable.Stats>
                        {typeof value === 'number' ? numberWithCommas(value) : '-'}
                    </ClipboardTable.Stats>
                ),
            },
            {
                Header: 'Engagement Rate',
                accessor: 'engagementRate',
                Cell: ({ value }) => (
                    <ClipboardTable.Stats>
                        {typeof value === 'number' && isFinite(value) ? `${toDecimalPoint(value * 100, 2)}%` : '-'}
                    </ClipboardTable.Stats>
                ),
            },
            {
                Header: 'Post',
                accessor: 'link',
                Cell: ({ value }) => {
                    if (!value) {
                        return null;
                    }

                    return <ClipboardTable.Link href={value}>🔗 Link</ClipboardTable.Link>;
                },
            },
        ],
        [campaignName, currency]
    );

    const caption = `${artistName} - ${projectName}`;

    return (
        <ClipboardTable
            columns={columns.filter((c) => includeCost || c.accessor !== 'cost')}
            data={items}
            caption={caption}
            renderFooter={() => {
                if (!totals) {
                    return null;
                }

                return (
                    <tfoot style={tfootStyles}>
                        <tr>
                            <td colSpan={4} style={totalsCellStyles}>
                                Total
                            </td>
                            {includeCost && (
                                <td style={tdStyles}>
                                    <ClipboardTable.Stats>
                                        {typeof totals.cost === 'number' ? asMoney(totals.cost, currency) : '-'}
                                    </ClipboardTable.Stats>
                                </td>
                            )}
                            <td style={tdStyles}>
                                <ClipboardTable.Stats>
                                    {typeof totals.views === 'number' ? numberWithCommas(totals.views) : '-'}
                                </ClipboardTable.Stats>
                            </td>
                            <td style={tdStyles}>
                                <ClipboardTable.Stats>
                                    {typeof totals.likes === 'number' ? numberWithCommas(totals.likes) : '-'}
                                </ClipboardTable.Stats>
                            </td>
                            <td style={tdStyles}>
                                <ClipboardTable.Stats>
                                    {typeof totals.comments === 'number' ? numberWithCommas(totals.comments) : '-'}
                                </ClipboardTable.Stats>
                            </td>
                            <td style={tdStyles}>
                                <ClipboardTable.Stats>
                                    {typeof totals.engagementRate === 'number' && isFinite(totals.engagementRate)
                                        ? `${toDecimalPoint(totals.engagementRate * 100, 2)}%`
                                        : '-'}
                                </ClipboardTable.Stats>
                            </td>
                        </tr>
                    </tfoot>
                );
            }}
        />
    );
};

export default YoutubeReportClipboardTable;
